import { useEffect, useState } from "react";
import { apiGet, ApiError, type Envelope } from "./client.js";
import { useSession } from "../auth/session.js";

export type ApiState<T> =
  | { readonly status: "loading" }
  | { readonly status: "error"; readonly error: ApiError }
  | { readonly status: "ready"; readonly envelope: Envelope<T> };

/**
 * Fetch an API resource for the current session. A 401 renews the session once (NX-3433): the new
 * token re-runs this effect and retries; only a failed renewal signs out.
 */
export function useApi<T>(path: string): ApiState<T> {
  const { session, onUnauthenticated } = useSession();
  const [state, setState] = useState<ApiState<T>>({ status: "loading" });
  const token = session?.token ?? null;

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading" });
    apiGet<T>(path, { token, signal: controller.signal })
      .then((envelope) => setState({ status: "ready", envelope }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        const apiError =
          error instanceof ApiError ? error : new ApiError(0, "unknown", "unexpected error");
        if (apiError.isUnauthenticated && token) void onUnauthenticated(token);
        setState({ status: "error", error: apiError });
      });
    return () => controller.abort();
  }, [path, token, onUnauthenticated]);

  return state;
}
